import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { MannerHeldType } from 'constants/loanApplication';
import { mannerHeldBplTypeOptions, mannerHeldNotBplTypeOptions } from 'constants/options';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';
import useRemoteFieldValueSync from 'hooks/useRemoteFieldValueSync';
import { getConditionalObject } from 'utils/data';
import { checkIsFormChanged } from 'utils/form';
import { jsonStringify } from 'utils/json';
import { getInvalidTabsValidationMessage, getIsBplCompanyNameVisible } from 'utils/loanApplication';
import { logger } from 'utils/logger';

import { getWarehouseBanksAction } from 'store/accounting/actions';
import { selectWarehouseBankOptions } from 'store/accounting/selectors';
import { selectClosingBusinessDays } from 'store/companies/selectors';
import { showMessageAction } from 'store/global/actions';
import {
  editClosingInformationAction,
  editFundingInformationAction,
  editLoanPropertyRelatedAction,
  editTrustInfoAction,
  getAttendantCompaniesAction,
  getChattelDetailsAction,
  getClosingInfoAction,
  getLoanApplicationCommonDataByApplicationIdAction,
  getLoanApplicationHeaderViewAction,
  saveChattelDetailsAction,
} from 'store/loanApplications/actions';
import {
  selectAttendantCompaniesFeeProviderCategoryIdMap,
  selectAttendantCompaniesOptions,
  selectChattelDetails,
  selectClosingCompanies,
  selectClosingDatesApplicationData,
  selectClosingInfo,
  selectEntityChangedPropsById,
  selectFundingInfo,
  selectIsBplLoanPurposeTypeFromApplication,
  selectIsChattelLoanFromPropertyAndLoanSummary,
  selectIsDisabledClosingInformationFields,
  selectIsDisabledLockGatedClosingFields,
  selectIsLoanClosingTwoPart,
  selectIsUnsecuredFromPropertyAndLoanSummary,
  selectKeyDates,
  selectLoanApplicationSubjectPropertyOccupancy,
  selectLoanApplicationSubjectPropertyState,
  selectLoanClosingChangedPropsById,
  selectMaturityCalcInputs,
  selectTrustInfo,
} from 'store/loanApplications/selectors';
import {
  getBorrowerVestingTypesAction,
  getFeeProviderCategoriesAction,
  getFeeProvidersAction,
} from 'store/system/actions';
import { selectBorrowerVestingTypesOptions } from 'store/system/selectors';

import {
  getInvalidSections,
  getUnvisitedFieldNames,
  mapTrustPendingChanges,
  resolveActiveSection,
  SECTION_HREF_MAP,
  SECTION_NAME_MAP,
} from '../EditDrawer.config';

import {
  CLOSING_INFORMATION_SECTIONS,
  LOCK_GATED_CLOSING_SECTIONS,
  TitleHolderFieldId,
  VALIDATION_TOAST_DURATION,
} from './Content.constants';
import {
  buildChattelDetailsSavePayload,
  getAllClosingAttendantCompanies,
  getChattelContractDatePayload,
  getIsAnyClosingCompanySectionChanged,
} from './Content.manufacturedHomeContacts.utils';
import { formWrapperStyles, wrapperStyles } from './Content.styles';
import {
  buildClosingPayload,
  buildClosingPayloadOptions,
  buildFundingPayload,
  buildTitleHolderApplicationData,
  buildTitleHolderPropertyPayload,
  buildTitleHolderTrustPayload,
  getInitialInterestCreditTimestamps,
  getIsRescissionApplicable,
  getProjectedMaturityTermMonths,
  getTimestamp,
} from './Content.utils';
import { useDateCascade } from './hooks/useDateCascade';
import { useFullConfig } from './hooks/useFullConfig';
import { useFundingHandlers } from './hooks/useFundingHandlers';
import { useManufacturedHomeContacts } from './hooks/useManufacturedHomeContacts';
import { usePendingChangesData } from './hooks/usePendingChangesData';
import { useSectionConfig } from './hooks/useSectionConfig';
import { useSectionValidation } from './hooks/useSectionValidation';
import { useTitleEscrowAttorneyCompanies } from './hooks/useTitleEscrowAttorneyCompanies';
import { useTitleHolderHandlers } from './hooks/useTitleHolderHandlers';
import { useTitleHolderRenderConfig } from './hooks/useTitleHolderRenderConfig';

const Content = ({
  setIsFormChanged,
  isSaving,
  setIsLoading,
  onClose,
  setOnSave,
  setIsSavingAllowed,
  additionalProps: { onCountPendingChanges, setPendingChanges, setInvalidSections },
}) => {
  const dispatch = useDispatch();
  const { applicationId, subtab: section } = useParams();
  const loanId = useLoanId();

  const closingInfo = useSelector(selectClosingInfo);
  const closingCompanies = useSelector(selectClosingCompanies);
  const fundingInfo = useSelector(selectFundingInfo);
  const chattelDetails = useSelector(selectChattelDetails);
  const warehouseBankOptions = useSelector(selectWarehouseBankOptions);
  const attendantCompanyOptions = useSelector(selectAttendantCompaniesOptions);
  const categoriesIds = useSelector(selectAttendantCompaniesFeeProviderCategoryIdMap);
  const loanPendingChangesData = useSelector(
    selectLoanClosingChangedPropsById({
      loanClosingId: closingInfo?.loanClosingId,
      loanId,
      applicationId,
    }),
  );

  const isTwoPartClosing = useSelector(selectIsLoanClosingTwoPart);
  const isBplLoanPurposeType = useSelector(selectIsBplLoanPurposeTypeFromApplication);
  const isUnsecured = useSelector(selectIsUnsecuredFromPropertyAndLoanSummary);
  const isChattel = useSelector(selectIsChattelLoanFromPropertyAndLoanSummary);
  const borrowerVestingOptions = useSelector(selectBorrowerVestingTypesOptions);
  const trustInfo = useSelector(selectTrustInfo);
  const trustPendingChanges = useSelector(selectEntityChangedPropsById(trustInfo?.id));
  const { earliestClosing: earliestClosingDate } = useSelector(selectKeyDates);
  const businessDays = useSelector(selectClosingBusinessDays);
  const loanApplication = useSelector(selectClosingDatesApplicationData);
  const subjectPropertyOccupancy = useSelector(selectLoanApplicationSubjectPropertyOccupancy);
  const subjectPropertyState = useSelector(selectLoanApplicationSubjectPropertyState);

  const isRescissionApplicable = useMemo(
    () => getIsRescissionApplicable({ loanApplication, subjectPropertyOccupancy }),
    [loanApplication, subjectPropertyOccupancy],
  );

  const isFieldsDisabled = useSelector(selectIsDisabledClosingInformationFields);
  const isLockGatedFieldsDisabled = useSelector(selectIsDisabledLockGatedClosingFields);
  const currentSection = resolveActiveSection({ section, isChattel });
  const isValidSection = Object.values(SECTION_HREF_MAP).includes(currentSection);
  const isSectionDisabled =
    (isFieldsDisabled && CLOSING_INFORMATION_SECTIONS.has(currentSection)) ||
    (isLockGatedFieldsDisabled && LOCK_GATED_CLOSING_SECTIONS.has(currentSection));

  const setFormRef = useRef(null);
  const setIsTouchedRef = useRef(null);
  const setInFormRef = useRef(null);
  const validateFieldRef = useRef(null);
  const formDataRef = useRef({});
  const formRef = useRef({});
  const shownMessageIdRef = useRef();
  const {
    titleCompany,
    escrowCompany,
    sellerAttorney,
    buyerAttorney,
    titleCompanyPendingChanges,
    escrowCompanyPendingChanges,
    titleLocationPendingChanges,
    escrowLocationPendingChanges,
    sellerAttorneyPendingChanges,
    sellerAttorneyLocationPendingChanges,
    buyerAttorneyPendingChanges,
    buyerAttorneyLocationPendingChanges,
    hasTitleCompany,
    setHasTitleCompany,
    hasEscrowCompany,
    setHasEscrowCompany,
    hasSellerAttorney,
    setHasSellerAttorney,
    hasBuyerAttorney,
    setHasBuyerAttorney,
    titleCompanyOptions,
    escrowCompanyOptions,
    sellerAttorneyOptions,
    buyerAttorneyOptions,
    onTitleCompanyChange,
    onEscrowCompanyChange,
    onSellerAttorneyChange,
    onBuyerAttorneyChange,
    onChangeTitleLocation,
    onChangeEscrowLocation,
  } = useTitleEscrowAttorneyCompanies({
    closingInfo,
    closingCompanies,
    attendantCompanyOptions,
    categoriesIds,
    isSaving,
    setIsLoading,
    setFormRef,
    setIsTouchedRef,
    formDataRef,
  });

  const {
    brokerCompany,
    dealerCompany,
    investorCompany,
    hasBroker,
    setHasBroker,
    hasDealer,
    setHasDealer,
    hasInvestor,
    setHasInvestor,
    brokerOptions,
    dealerOptions,
    investorOptions,
    onBrokerChange,
    onDealerChange,
    onInvestorChange,
  } = useManufacturedHomeContacts({ closingCompanies, setFormRef, setIsTouchedRef });

  const manufacturedHomeContactsProps = useMemo(
    () => ({
      brokerOptions,
      dealerOptions,
      investorOptions,
      onBrokerChange,
      onDealerChange,
      onInvestorChange,
      hasBroker,
      hasDealer,
      hasInvestor,
    }),
    [
      brokerOptions,
      dealerOptions,
      investorOptions,
      onBrokerChange,
      onDealerChange,
      onInvestorChange,
      hasBroker,
      hasDealer,
      hasInvestor,
    ],
  );

  const {
    onFeeProviderChange,
    onOpenFeeProviderLocationTable,
    onChangeAdvanceAmountPercentDependantField,
    isChangeLocationButtonDisabled,
    isChangeLocationButtonVisible,
  } = useFundingHandlers({
    formRef,
    setFormRef,
    setIsTouchedRef,
    validateFieldRef,
    isSaving,
    setIsLoading,
    feeProviderOptions: attendantCompanyOptions,
  });

  const {
    borrowers,
    titleHolders,
    isTrustSectionVisible,
    isBorrowersOrTitleHoldersExists,
    onTitleHolderAdd,
    onTitleHolderDelete,
    onAddTrust,
    onRemoveTrust,
    onCopyBorrowers,
    onPopulateVesting,
  } = useTitleHolderHandlers({
    setIsLoading,
    setFormRef,
    mannerHeldOptions: mannerHeldNotBplTypeOptions,
    initialFormValues: closingInfo,
  });

  const [currentCloseAt, setCurrentCloseAt] = useState(() => getTimestamp(closingInfo?.closeAt));
  const initialCreditDates = getInitialInterestCreditTimestamps(closingInfo);
  const [currentNoteDate, setCurrentNoteDate] = useState(initialCreditDates.noteDate);
  const [currentFundingDate, setCurrentFundingDate] = useState(initialCreditDates.fundingDate);

  const filteredMannerHeldOptions = useMemo(
    () =>
      isBplLoanPurposeType
        ? mannerHeldBplTypeOptions
        : mannerHeldNotBplTypeOptions.filter(({ value }) => {
            const isExcludedWhenNoBorrowersOrTitleHolders =
              [MannerHeldType.LIFE_ESTATE, MannerHeldType.OTHER].includes(value) &&
              !isBorrowersOrTitleHoldersExists;

            return !isExcludedWhenNoBorrowersOrTitleHolders;
          }),
    [isBplLoanPurposeType, isBorrowersOrTitleHoldersExists],
  );

  const sectionConfig = useSectionConfig({
    currentSection,
    isValidSection,
    attendantCompanyOptions,
    titleCompanyOptions,
    escrowCompanyOptions,
    onTitleCompanyChange,
    onEscrowCompanyChange,
    onChangeTitleLocation,
    onChangeEscrowLocation,
    hasTitleCompany,
    hasEscrowCompany,
    sellerAttorneyOptions,
    buyerAttorneyOptions,
    onSellerAttorneyChange,
    onBuyerAttorneyChange,
    hasSellerAttorney,
    hasBuyerAttorney,
    warehouseBankOptions,
    onFeeProviderChange,
    onOpenFeeProviderLocationTable,
    isChangeLocationButtonDisabled,
    isChangeLocationButtonVisible,
    onChangeAdvanceAmountPercentDependantField,
    isTwoPartClosing,
    borrowerVestingOptions,
    filteredMannerHeldOptions,
    isTrustSectionVisible,
    isBplLoanPurposeType,
    earliestClosingDate,
    currentCloseAt,
    currentNoteDate,
    businessDays,
    isUnsecured,
    isRescissionApplicable,
    isChattel,
    chattelFundingDate: currentFundingDate,
    manufacturedHomeContactsProps,
  });

  const { fullConfig, initialFormValues, fieldsConfig, observableFieldsNames, schema } =
    useFullConfig({
      isTwoPartClosing,
      isUnsecured,
      earliestClosingDate,
      currentCloseAt,
      attendantCompanyOptions,
      titleCompanyOptions,
      escrowCompanyOptions,
      onTitleCompanyChange,
      onEscrowCompanyChange,
      onChangeTitleLocation,
      onChangeEscrowLocation,
      hasTitleCompany,
      hasEscrowCompany,
      sellerAttorneyOptions,
      buyerAttorneyOptions,
      onSellerAttorneyChange,
      onBuyerAttorneyChange,
      hasSellerAttorney,
      hasBuyerAttorney,
      currentNoteDate,
      businessDays,
      warehouseBankOptions,
      onFeeProviderChange,
      onOpenFeeProviderLocationTable,
      isChangeLocationButtonDisabled,
      isChangeLocationButtonVisible,
      onChangeAdvanceAmountPercentDependantField,
      borrowerVestingOptions,
      filteredMannerHeldOptions,
      isTrustSectionVisible,
      isBplLoanPurposeType,
      closingInfo,
      fundingInfo,
      trustInfo,
      titleCompany,
      escrowCompany,
      sellerAttorney,
      buyerAttorney,
      isRescissionApplicable,
      isChattel,
      chattelFundingDate: currentFundingDate,
      brokerCompany,
      dealerCompany,
      investorCompany,
      chattelDetails,
      ...manufacturedHomeContactsProps,
    });

  const {
    form,
    handleChange,
    validationErrors,
    setInForm,
    isFormChanged,
    validateForm,
    validateField,
    setInitialFormValues,
    setForm,
    setIsTouched,
    setSchema,
    setExcludedValidationPaths,
    formPendingChanges,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  setIsTouchedRef.current = setIsTouched;
  formRef.current = form;

  setFormRef.current = setForm;
  setInFormRef.current = setInForm;
  validateFieldRef.current = validateField;

  const isMannerHeldDisabled = isSaving || isTrustSectionVisible;
  const isCompanyNameFieldVisible = getIsBplCompanyNameVisible({
    isBplLoanPurposeType,
    mannerHeld: form?.mannerHeld?.value,
  });

  const { renderConfig, disabledConfig } = useTitleHolderRenderConfig({
    config: sectionConfig,
    currentSection,
    borrowers,
    titleHolders,
    isTrustSectionVisible,
    isBplLoanPurposeType,
    mannerHeldOptions: filteredMannerHeldOptions,
    isMannerHeldDisabled,
    isCompanyNameFieldVisible,
    borrowerVestingOptions,
    onTitleHolderAdd,
    onTitleHolderDelete,
    onAddTrust,
    onRemoveTrust,
    onCopyBorrowers,
    onPopulateVesting,
    formRef,
    setFormRef,
    setInFormRef,
  });

  const { prepaidInterestCreditData } = closingInfo ?? {};
  const { hasInterestCredit = false } = prepaidInterestCreditData ?? {};

  const maturityCalcInputs = useSelector(selectMaturityCalcInputs);

  const maturityTermMonths = useMemo(
    () =>
      getProjectedMaturityTermMonths({
        maturityCalcInputs,
        firstPaymentDate: prepaidInterestCreditData?.interestCreditFirstPaymentDate,
        maturityDate: closingInfo?.maturityDate,
      }),
    [
      maturityCalcInputs,
      prepaidInterestCreditData?.interestCreditFirstPaymentDate,
      closingInfo?.maturityDate,
    ],
  );

  useDateCascade({
    form,
    setInForm,
    currentSection,
    hasInterestCredit,
    businessDays,
    loanApplication,
    subjectPropertyOccupancy,
    subjectPropertyState,
    setCurrentCloseAt,
    setCurrentNoteDate,
    setCurrentFundingDate,
    maturityTermMonths,
    isChattel,
  });

  const { validateSections } = useSectionValidation({
    titleCompany,
    escrowCompany,
    sellerAttorney,
    buyerAttorney,
    isTrustSectionVisible,
  });

  const { remotePendingChanges, getCleanFormForSave, remoteInjectedFormKeys } =
    useRemoteFieldValueSync({
      config: fullConfig,
      setForm,
      setInitialFormValues,
    });

  const visitedSectionsRef = useRef(new Set());

  const mappedTrustPendingChanges = useMemo(
    () => mapTrustPendingChanges(trustPendingChanges),
    [trustPendingChanges],
  );

  const { mergedPendingChanges } = usePendingChangesData({
    form,
    observableFieldsNames,
    formPendingChanges,
    remotePendingChanges,
    loanPendingChangesData,
    trustPendingChanges: mappedTrustPendingChanges,
    titleCompanyPendingChanges,
    titleLocationPendingChanges,
    escrowCompanyPendingChanges,
    escrowLocationPendingChanges,
    sellerAttorneyPendingChanges,
    sellerAttorneyLocationPendingChanges,
    buyerAttorneyPendingChanges,
    buyerAttorneyLocationPendingChanges,
  });

  const onSave = useCallback(async () => {
    shownMessageIdRef.current = null;

    if (!(await validateForm({ noFormChangedValidate: true }))) {
      return;
    }

    const allFormData = getCleanFormForSave(form);
    const visited = visitedSectionsRef.current;
    const savePromises = [];

    if (!validateSections(allFormData, visited)) {
      return;
    }

    setIsLoading(true);

    if (
      visited.has(SECTION_HREF_MAP.SCHEDULING) ||
      visited.has(SECTION_HREF_MAP.CLOSING_FUNDING_DATES) ||
      visited.has(SECTION_HREF_MAP.TITLE_COMPANY) ||
      visited.has(SECTION_HREF_MAP.MANUFACTURED_HOME_CONTACTS)
    ) {
      const closingPayload = buildClosingPayload(
        allFormData,
        buildClosingPayloadOptions({
          hasInterestCredit,
          loanApplication,
          subjectPropertyState,
          businessDays,
          isLockGatedFieldsDisabled,
          allFormData,
          initialFormValues,
        }),
      );
      // NOTE: as of this writing the closing save endpoint hard-caps the attendant
      // company list at 4 entries and its location DTO has no Fax/AccountManager/
      // Channel/Salesperson/ContactCategory fields — see IMP-490 handoff notes.
      const closingAttendantCompanies = getAllClosingAttendantCompanies({
        formData: allFormData,
        titleCompany,
        escrowCompany,
        sellerAttorney,
        buyerAttorney,
        brokerCompany,
        dealerCompany,
        investorCompany,
      });

      const isCompanyChanged = getIsAnyClosingCompanySectionChanged({
        formData: allFormData,
        initialFormData: initialFormValues,
        titleCompany,
        escrowCompany,
        sellerAttorney,
        buyerAttorney,
        brokerCompany,
        dealerCompany,
        investorCompany,
      });

      savePromises.push(
        dispatch(
          editClosingInformationAction({
            applicationId,
            ...closingPayload,
            ...getConditionalObject(isCompanyChanged, { closingAttendantCompanies }),
            requestMetadata: { tabId: LoanApplicationTabId.CLOSING },
          }),
        ),
      );
    }

    if (visited.has(SECTION_HREF_MAP.FUNDING_INFORMATION)) {
      const fundingPayload = buildFundingPayload(allFormData);
      const initialFundingPayload = buildFundingPayload(initialFormValues);
      const isFundingDataChanged = checkIsFormChanged({
        currentForm: fundingPayload,
        initialForm: initialFundingPayload,
      });

      if (isFundingDataChanged) {
        savePromises.push(
          dispatch(
            editFundingInformationAction({
              loanId,
              ...fundingPayload,
              tabId: LoanApplicationTabId.CLOSING,
            }),
          ),
        );
      }
    }

    if (visited.has(SECTION_HREF_MAP.MANUFACTURED_HOME_CONTACTS)) {
      const contractDatePayload = getChattelContractDatePayload(allFormData);
      const initialContractDatePayload = getChattelContractDatePayload(initialFormValues);
      const isContractDateChanged = checkIsFormChanged({
        currentForm: contractDatePayload,
        initialForm: initialContractDatePayload,
      });

      if (isContractDateChanged) {
        savePromises.push(
          dispatch(
            saveChattelDetailsAction({
              applicationId,
              chattelDetails: buildChattelDetailsSavePayload({
                chattelDetails,
                contractDate: contractDatePayload.contractDate,
              }),
            }),
          ),
        );
      }
    }

    if (visited.has(SECTION_HREF_MAP.TITLE_HOLDER)) {
      const initialTitleHolderData = buildTitleHolderApplicationData({ closingInfo, trustInfo });

      // Order of PUT requests is important — trust must be saved before property
      const saveTitleHolderData = async () => {
        if (isTrustSectionVisible) {
          const trustPayload = buildTitleHolderTrustPayload(allFormData);
          const initialTrustPayload = buildTitleHolderTrustPayload(initialTitleHolderData);
          const isTrustDataChanged = checkIsFormChanged({
            currentForm: trustPayload,
            initialForm: initialTrustPayload,
          });

          if (isTrustDataChanged) {
            await dispatch(editTrustInfoAction({ applicationId, ...trustPayload }));
          }
        }

        const propertyPayload = buildTitleHolderPropertyPayload(allFormData);
        const initialPropertyPayload = buildTitleHolderPropertyPayload(initialTitleHolderData);
        const isPropertyDataChanged = checkIsFormChanged({
          currentForm: propertyPayload,
          initialForm: initialPropertyPayload,
        });

        if (isPropertyDataChanged) {
          await dispatch(editLoanPropertyRelatedAction({ applicationId, ...propertyPayload }));
        }
      };

      savePromises.push(saveTitleHolderData());
    }

    return Promise.all(savePromises)
      .then(() => {
        if (visited.has(SECTION_HREF_MAP.TITLE_HOLDER)) {
          dispatch(getLoanApplicationHeaderViewAction({ applicationId }));
          dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId }));
        }

        dispatch(
          showMessageAction({
            message: 'Closing information saved successfully',
            variant: 'success',
          }),
        );
        onClose();
        dispatch(getClosingInfoAction({ loanId }));
      })
      .catch(error => {
        logger.error(error);
        dispatch(
          showMessageAction({ message: 'Failed to save closing information', variant: 'error' }),
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    validateForm,
    getCleanFormForSave,
    form,
    validateSections,
    setIsLoading,
    hasInterestCredit,
    isLockGatedFieldsDisabled,
    loanApplication,
    subjectPropertyState,
    businessDays,
    initialFormValues,
    titleCompany,
    escrowCompany,
    sellerAttorney,
    buyerAttorney,
    brokerCompany,
    dealerCompany,
    investorCompany,
    chattelDetails,
    dispatch,
    applicationId,
    loanId,
    closingInfo,
    trustInfo,
    isTrustSectionVisible,
    onClose,
  ]);

  useEffect(() => {
    setHasTitleCompany(!!titleCompany?.attendantCompanyId);
    setHasEscrowCompany(!!escrowCompany?.attendantCompanyId);
    setHasSellerAttorney(!!sellerAttorney?.attendantCompanyId);
    setHasBuyerAttorney(!!buyerAttorney?.attendantCompanyId);
    setHasBroker(!!brokerCompany?.attendantCompanyId);
    setHasDealer(!!dealerCompany?.attendantCompanyId);
    setHasInvestor(!!investorCompany?.attendantCompanyId);
  }, [
    titleCompany?.attendantCompanyId,
    escrowCompany?.attendantCompanyId,
    sellerAttorney?.attendantCompanyId,
    buyerAttorney?.attendantCompanyId,
    brokerCompany?.attendantCompanyId,
    dealerCompany?.attendantCompanyId,
    investorCompany?.attendantCompanyId,
    setHasTitleCompany,
    setHasEscrowCompany,
    setHasSellerAttorney,
    setHasBuyerAttorney,
    setHasBroker,
    setHasDealer,
    setHasInvestor,
  ]);

  useEffect(() => {
    if (isValidSection) {
      visitedSectionsRef.current.add(currentSection);
    }
  }, [currentSection, isValidSection]);

  useEffect(() => {
    const hiddenFieldPaths = isCompanyNameFieldVisible ? [] : [TitleHolderFieldId.BPL_COMPANY_NAME];
    const unvisitedFieldNames = getUnvisitedFieldNames(visitedSectionsRef.current);

    setExcludedValidationPaths([
      ...remoteInjectedFormKeys,
      ...hiddenFieldPaths,
      ...unvisitedFieldNames,
    ]);
  }, [
    remoteInjectedFormKeys,
    isCompanyNameFieldVisible,
    setExcludedValidationPaths,
    currentSection,
  ]);

  useEffect(() => {
    formDataRef.current = form;
  }, [form]);

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    Promise.all([
      dispatch(getWarehouseBanksAction()),
      dispatch(getAttendantCompaniesAction()),
      dispatch(getFeeProvidersAction()),
      dispatch(getFeeProviderCategoriesAction()),
      dispatch(getBorrowerVestingTypesAction()),
    ]).finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading]);

  // Chattel details load in their own effect keyed on isChattel so a late chattel
  // determination (property summary resolving after mount) doesn't re-fire the base
  // batch or flash the loader. Seeds the Contract Date field's initial value AND the
  // store row saveChattelDetailsAction reads for its create-vs-update branch — an empty
  // store when a row already exists makes the save wrongly POST → ApplicationId+CompanyId
  // unique index (409).
  useEffectSafeStrictMode(() => {
    if (isChattel) {
      dispatch(getChattelDetailsAction({ applicationId }));
    }
  }, [dispatch, isChattel, applicationId]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  useEffect(() => {
    setInitialFormValues(initialFormValues);
    setForm(prevForm => {
      const merged = { ...prevForm };

      Object.entries(initialFormValues).forEach(([key, defaultValue]) => {
        if (!(key in merged) || _.isUndefined(merged[key])) {
          merged[key] = defaultValue;
        }
      });

      return merged;
    });
  }, [initialFormValues, setForm, setInitialFormValues]);

  useEffect(() => {
    if (isFormChanged) {
      setIsFormChanged(true);
    }
  }, [isFormChanged, setIsFormChanged]);

  useEffect(() => {
    onCountPendingChanges?.(mergedPendingChanges);
    setPendingChanges?.(mergedPendingChanges);
  }, [mergedPendingChanges, onCountPendingChanges, setPendingChanges]);

  useEffect(() => {
    const sections = getInvalidSections(validationErrors);
    setInvalidSections?.(sections);
    setIsSavingAllowed?.(!sections.length);

    const otherSections = sections.filter(s => s !== currentSection);

    if (!otherSections.length) {
      return;
    }

    const toastId = jsonStringify(otherSections);

    if (shownMessageIdRef.current !== toastId) {
      shownMessageIdRef.current = toastId;
      const tabNames = otherSections.map(s => SECTION_NAME_MAP[s]).filter(Boolean);
      dispatch(
        showMessageAction({
          message: getInvalidTabsValidationMessage(tabNames),
          variant: 'error',
          id: toastId,
          duration: VALIDATION_TOAST_DURATION,
        }),
      );
    }
  }, [validationErrors, currentSection, setInvalidSections, setIsSavingAllowed, dispatch]);

  useEffect(() => {
    setOnSave(() => onSave);
  }, [setOnSave, onSave]);

  return (
    <Box sx={wrapperStyles}>
      {isValidSection && (
        <Box sx={formWrapperStyles}>
          <FormGenerator
            config={isSectionDisabled ? disabledConfig : renderConfig}
            form={form}
            formName={FormNames.CLOSING_INFORMATION}
            groupGap={24}
            handleChange={handleChange}
            isLoading={isSaving}
            numberOfColumns={3}
            pendingChanges={mergedPendingChanges}
            setInForm={setInForm}
            validateField={validateField}
            validationErrors={validationErrors}
          />
        </Box>
      )}
    </Box>
  );
};

Content.propTypes = {
  isSaving: PropTypes.bool.isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  setOnSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  setIsSavingAllowed: PropTypes.func,
  additionalProps: PropTypes.shape({
    setInvalidSections: PropTypes.func,
    setPendingChanges: PropTypes.func,
    onCountPendingChanges: PropTypes.func,
  }),
  ...WithLoadingPropTypes,
};

export default memo(withLoading(Content));
